import { useTranslation } from 'react-i18next';
import {
  ScheduleRuleTypeLabels,
  ScheduleRuleTypeDescriptions,
  WeekdayLabels,
  WeekdayOptions,
  OrdinalOptions,
  OrdinalLabels,
  formatScheduleRule,
} from '../../../utils/scheduleRuleConstants';
import { getRuleIcon } from './scheduleRuleDisplay';
import { DayMonthSelect, WeekdayPicker } from './ScheduleRuleParts';
import type { ScheduleRuleForm } from './useScheduleRuleForm';

interface ScheduleRuleFieldsProps {
  form: ScheduleRuleForm;
  /** Smaller spacing/typography for the inline editor. */
  compact?: boolean;
}

/**
 * Shared rule-builder inputs: type selector, weekday multiselect (Weekly),
 * ordinal + weekday (Monthly), day-precise window (BiWeekly/Monthly/Seasonal), and a preview.
 */
export function ScheduleRuleFields({ form, compact = false }: ScheduleRuleFieldsProps) {
  const { t } = useTranslation(['tasks', 'common']);

  const labelClass = compact
    ? 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5'
    : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';
  const selectClass = compact
    ? 'w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
    : 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
  const iconSize = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const renderMonthDay = (
    labelKey: string,
    month: number,
    setMonth: (m: number) => void,
    day: number,
    setDay: (d: number) => void,
  ) => (
    <DayMonthSelect
      label={t(labelKey)}
      month={month}
      day={day}
      onChange={(m, d) => { setMonth(m); setDay(d); }}
      labelClass={labelClass}
      selectClass={selectClass}
    />
  );

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {/* Rule Type */}
      <div>
        <label className={labelClass}>{t('tasks:scheduleRuleForm.type')}</label>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(ScheduleRuleTypeLabels).map(([value, label]) => {
            const numVal = parseInt(value);
            const isSelected = form.ruleType === numVal;
            return (
              <button
                key={value}
                type="button"
                onClick={() => form.setRuleType(numVal)}
                className={`flex items-center gap-2 rounded-md border text-sm font-medium transition-colors ${
                  compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2.5'
                } ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {getRuleIcon(numVal, iconSize)}
                {label}
              </button>
            );
          })}
        </div>
        <p className={`mt-2 text-xs text-gray-500 dark:text-gray-400`}>
          {ScheduleRuleTypeDescriptions[form.ruleType]}
        </p>
      </div>

      {/* Weekly: multi-weekday */}
      {form.needsWeekdays && (
        <div>
          <label className={labelClass}>{t('tasks:scheduleRuleForm.weekdays')}</label>
          <WeekdayPicker value={form.weekdays} onToggle={form.toggleWeekday} compact={compact} />
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            {t('tasks:scheduleRuleForm.weekdaysOptionalHint')}
          </p>
        </div>
      )}

      {/* Monthly: any day vs. ordinal + weekday */}
      {form.needsMonthly && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t('tasks:scheduleRuleForm.ordinal')}</label>
            <select
              value={form.monthlyAnyDay ? '' : form.ordinal}
              onChange={(e) => {
                if (e.target.value === '') {
                  form.setMonthlyAnyDay(true);
                } else {
                  form.setMonthlyAnyDay(false);
                  form.setOrdinal(parseInt(e.target.value));
                }
              }}
              className={selectClass}
              aria-label={t('tasks:scheduleRuleForm.ordinal')}
            >
              <option value="">{t('tasks:scheduleRuleForm.anyDay')}</option>
              {OrdinalOptions.map((o) => (
                <option key={o} value={o}>{OrdinalLabels[o]}</option>
              ))}
            </select>
          </div>
          {!form.monthlyAnyDay && (
            <div>
              <label className={labelClass}>{t('tasks:scheduleRuleForm.weekday')}</label>
              <select
                value={form.monthlyWeekday}
                onChange={(e) => form.setMonthlyWeekday(parseInt(e.target.value))}
                className={selectClass}
                aria-label={t('tasks:scheduleRuleForm.weekday')}
              >
                {WeekdayOptions.map((day) => (
                  <option key={day} value={day}>{WeekdayLabels[day]}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Monthly: optional window toggle ("all year" by default) */}
      {form.windowOptional && (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={form.monthlyHasWindow}
            onChange={(e) => form.setMonthlyHasWindow(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          {t('tasks:scheduleRuleForm.limitWindow')}
          {!form.monthlyHasWindow && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({t('tasks:scheduleRuleForm.allYear')})
            </span>
          )}
        </label>
      )}

      {/* Window (BiWeekly / Seasonal, Monthly opt-in) */}
      {form.needsWindow && (
        <div className="grid grid-cols-2 gap-4">
          {renderMonthDay(
            'tasks:scheduleRuleForm.windowStart',
            form.windowFromMonth, form.setWindowFromMonth,
            form.windowFromDay, form.setWindowFromDay,
          )}
          {renderMonthDay(
            'tasks:scheduleRuleForm.windowEnd',
            form.windowToMonth, form.setWindowToMonth,
            form.windowToDay, form.setWindowToDay,
          )}
        </div>
      )}

      {/* Preview */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('tasks:scheduleRuleForm.preview')}</p>
        <div className="flex items-center gap-2">
          {getRuleIcon(form.ruleType, iconSize)}
          <span className="font-medium text-gray-900 dark:text-white text-sm">
            {formatScheduleRule(form.preview())}
          </span>
        </div>
      </div>
    </div>
  );
}
